import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const CommentDockContext = createContext(null);
const STORAGE_PREFIX = 'commentDockThreads:';
const MINIMIZED_PREFIX = 'commentDockMinimized:';
const ORDER_KEY_PREFIX = 'order:';
let lastKnownUserKey = null;
let lastKnownMinimizedKey = null;

export function getCommentThreadKey({ orderLineId, quoteId, poLineId } = {}) {
    if (quoteId != null && quoteId !== '') return `quote:${quoteId}`;
    if (poLineId != null && poLineId !== '') return `poLine:${poLineId}`;
    if (orderLineId != null && orderLineId !== '') return `orderLine:${orderLineId}`;
    return null;
}

/** Key for a chat created from a notification before we know which order line it belongs to. */
export function getOrderThreadKey(orderNumber) {
    if (orderNumber == null || orderNumber === '') return null;
    return `${ORDER_KEY_PREFIX}${orderNumber}`;
}

export function isOrderOnlyThreadKey(key) {
    return typeof key === 'string' && key.startsWith(ORDER_KEY_PREFIX);
}

/** True when a thread is about the given order (by line id, or by order number). */
export function threadMatchesOrder(thread, { orderNumber, orderLineId } = {}) {
    if (!thread) return false;
    if (orderLineId != null && orderLineId !== '' && thread.orderLineId != null
        && String(thread.orderLineId) === String(orderLineId)) {
        return true;
    }
    if (orderNumber != null && orderNumber !== '' && thread.orderNumber != null
        && String(thread.orderNumber) === String(orderNumber)) {
        return true;
    }
    return false;
}

export function formatThreadTitle({ orderNumber, quoteId, poLineId, orderLineId } = {}) {
    if (orderNumber) return String(orderNumber);
    if (quoteId) return String(quoteId);
    if (poLineId) return `PO ${poLineId}`;
    if (orderLineId) return String(orderLineId);
    return 'Comments';
}

function getCurrentUserKey() {
    try {
        const email = (
            localStorage.getItem('userEmail')
            || localStorage.getItem('email')
            || ''
        ).trim().toLowerCase();
        if (!email) return null;
        const key = `${STORAGE_PREFIX}${email}`;
        lastKnownUserKey = key;
        return key;
    } catch (err) {
        return lastKnownUserKey;
    }
}

function getPersistKey() {
    return getCurrentUserKey() || lastKnownUserKey;
}

/** Separate from the thread blob so a later save cannot forget that a chat was minimized. */
function getMinimizedKey() {
    const threadKey = getPersistKey();
    if (!threadKey) return lastKnownMinimizedKey;
    const email = threadKey.slice(STORAGE_PREFIX.length);
    const key = `${MINIMIZED_PREFIX}${email}`;
    lastKnownMinimizedKey = key;
    return key;
}

function readMinimizedKeys() {
    try {
        const key = getMinimizedKey();
        if (!key) return new Set();
        const parsed = JSON.parse(localStorage.getItem(key) || '[]');
        return new Set((Array.isArray(parsed) ? parsed : []).map(String));
    } catch (err) {
        return new Set();
    }
}

function writeMinimizedKeys(keys) {
    const key = getMinimizedKey();
    if (!key) return;
    try {
        localStorage.setItem(key, JSON.stringify([...keys]));
    } catch (err) {
        /* ignore quota / private-mode failures */
    }
}

function rememberMinimized(key) {
    if (!key) return;
    const keys = readMinimizedKeys();
    keys.add(String(key));
    writeMinimizedKeys(keys);
}

function forgetMinimized(...ids) {
    const keys = readMinimizedKeys();
    let changed = false;
    ids.filter(Boolean).forEach((id) => {
        if (keys.delete(String(id))) changed = true;
    });
    if (changed) writeMinimizedKeys(keys);
}

function serializeThread(thread) {
    if (!thread || !thread.key) return null;
    const pos = thread.pos
        && Number.isFinite(thread.pos.left)
        && Number.isFinite(thread.pos.top)
        ? { left: thread.pos.left, top: thread.pos.top }
        : null;
    return {
        key: thread.key,
        orderLineId: thread.orderLineId ?? null,
        quoteId: thread.quoteId ?? null,
        poLineId: thread.poLineId ?? null,
        sourcePage: thread.sourcePage || 'orders',
        title: thread.title || 'Comments',
        subtitle: thread.subtitle || '',
        lineInfo: thread.lineInfo || '',
        orderNumber: thread.orderNumber,
        customerId: thread.customerId,
        comments: [],
        minimized: !!thread.minimized,
        pos,
        createdAt: thread.createdAt || Date.now(),
    };
}

function loadPersistedThreads() {
    try {
        const key = getCurrentUserKey();
        if (!key) return [];
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Comment bodies are never persisted, so every restored chat reloads them on open.
        // A page load never restores an open window. The chat stays in the tray until
        // the comment icon, the tray, or the bell opens it.
        return parsed.map(serializeThread).filter(Boolean).map((t) => {
            rememberMinimized(t.key);
            return {
                ...t,
                pendingLoad: true,
                minimized: true,
            };
        });
    } catch (err) {
        return [];
    }
}

function persistThreads(threads) {
    const key = getPersistKey();
    if (!key) return;
    const payload = (threads || []).map(serializeThread).filter(Boolean);
    try {
        localStorage.setItem(key, JSON.stringify(payload));
        return;
    } catch (err) {
        /* quota — retry without comment bodies so visibility still survives */
    }
    try {
        localStorage.setItem(
            key,
            JSON.stringify(payload.map((thread) => ({ ...thread, comments: [] })))
        );
    } catch (err) {
        /* ignore private-mode / quota failures */
    }
}

/** Apply a shallow patch; return the same object when nothing changes so effects don't loop. */
function patchObject(target, patch) {
    const changed = Object.keys(patch).some((k) => target[k] !== patch[k]);
    return changed ? { ...target, ...patch } : target;
}

export function CommentDockProvider({ children }) {
    const [threads, setThreads] = useState(() => loadPersistedThreads());
    const handlersRef = useRef({});
    const threadsRef = useRef([]);
    threadsRef.current = threads;

    useEffect(() => {
        persistThreads(threads);
    }, [threads]);

    useEffect(() => {
        const onBeforeUnload = () => persistThreads(threadsRef.current);
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            persistThreads(threadsRef.current);
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, []);

    const registerHandlers = useCallback((key, handlers = {}) => {
        if (!key) return;
        handlersRef.current[key] = {
            onAddComment: handlers.onAddComment,
            onDeleteComment: handlers.onDeleteComment,
            onResolveThread: handlers.onResolveThread,
            onVisibleChange: handlers.onVisibleChange,
        };
    }, []);

    const getHandlers = useCallback((key) => handlersRef.current[key] || {}, []);

    const isThreadOpen = useCallback((key) => {
        if (!key) return false;
        return threadsRef.current.some((t) => t.key === key && !t.minimized);
    }, []);

    const openThread = useCallback((payload = {}) => {
        const key = payload.key || getCommentThreadKey(payload);
        if (!key) return;

        registerHandlers(key, payload);

        setThreads((prev) => {
            // A chat that was created from a notification (keyed by order number) is the
            // same conversation as this order line's thread: replace it, don't duplicate it.
            const placeholder = !isOrderOnlyThreadKey(key)
                ? prev.find((t) => t.key !== key && isOrderOnlyThreadKey(t.key)
                    && threadMatchesOrder(t, { orderNumber: payload.orderNumber }))
                : null;
            const base = placeholder ? prev.filter((t) => t !== placeholder) : prev;
            const existing = base.find((t) => t.key === key) || placeholder;
            const hasComments = Array.isArray(payload.comments);
            // Only the comment icon, the tray, or the bell pass expand. A refresh must not.
            const expand = payload.expand === true;
            if (expand) forgetMinimized(key, existing?.key);
            const stayMinimized = !expand && (
                !!existing?.minimized
                || readMinimizedKeys().has(key)
                || (existing?.key ? readMinimizedKeys().has(existing.key) : false)
            );
            const nextThread = {
                key,
                orderLineId: payload.orderLineId ?? existing?.orderLineId,
                quoteId: payload.quoteId ?? existing?.quoteId,
                poLineId: payload.poLineId ?? existing?.poLineId,
                sourcePage: payload.sourcePage ?? existing?.sourcePage ?? 'orders',
                title: payload.title || existing?.title || formatThreadTitle(payload),
                subtitle: payload.subtitle || existing?.subtitle || '',
                lineInfo: payload.lineInfo ?? existing?.lineInfo ?? '',
                orderNumber: payload.orderNumber ?? existing?.orderNumber,
                customerId: payload.customerId ?? existing?.customerId,
                comments: hasComments ? payload.comments : (existing?.comments || []),
                minimized: stayMinimized,
                pos: payload.pos ?? existing?.pos ?? null,
                createdAt: existing?.createdAt || Date.now(),
                highlightCount: payload.highlightCount ?? existing?.highlightCount ?? 0,
                pendingLoad: hasComments ? false : (payload.pendingLoad ?? existing?.pendingLoad ?? false),
                loading: existing?.loading || false,
                loadError: existing?.loadError || null,
            };

            if (base.some((t) => t.key === key)) {
                return base.map((t) => (t.key === key ? nextThread : t));
            }
            return [...base, nextThread];
        });
    }, [registerHandlers]);

    /** Add a minimized chat unless one already exists. Never reopens or moves a chat. */
    const ensureThread = useCallback((payload = {}) => {
        const key = payload.key || getCommentThreadKey(payload) || getOrderThreadKey(payload.orderNumber);
        if (!key) return;
        setThreads((prev) => {
            const quoteId = payload.quoteId != null && payload.quoteId !== '' ? payload.quoteId : null;
            const poLineId = payload.poLineId != null && payload.poLineId !== '' ? payload.poLineId : null;
            // A chat created from a notification before we knew it was a quote is the same conversation.
            const placeholder = quoteId
                ? prev.find((t) => t.key !== key && isOrderOnlyThreadKey(t.key)
                    && String(t.orderNumber) === String(payload.orderNumber || quoteId))
                : null;
            const exists = prev.some((t) => {
                if (t.key === key) return true;
                if (quoteId != null) {
                    return t.quoteId != null && String(t.quoteId) === String(quoteId);
                }
                if (poLineId != null) {
                    return t.poLineId != null && String(t.poLineId) === String(poLineId);
                }
                if (payload.sourcePage === 'purchase' && payload.orderNumber) {
                    return (t.poLineId != null || t.sourcePage === 'purchase')
                        && t.orderNumber != null
                        && String(t.orderNumber) === String(payload.orderNumber);
                }
                // A quote or PO chat that happens to share a number must not swallow an order chat.
                if (t.quoteId != null && t.quoteId !== '') return false;
                if (t.poLineId != null && t.poLineId !== '') return false;
                return !placeholder && threadMatchesOrder(t, payload);
            });
            if (exists) return prev;
            const nextThread = {
                key,
                orderLineId: quoteId || poLineId ? undefined : payload.orderLineId,
                quoteId,
                poLineId: payload.poLineId,
                sourcePage: payload.sourcePage || (quoteId ? 'quotes' : (poLineId ? 'purchase' : 'orders')),
                title: payload.title || formatThreadTitle(payload),
                subtitle: payload.subtitle || '',
                lineInfo: '',
                orderNumber: payload.orderNumber,
                customerId: payload.customerId,
                comments: Array.isArray(payload.comments) ? payload.comments : [],
                minimized: true,
                pos: null,
                createdAt: placeholder?.createdAt || Date.now(),
                highlightCount: 0,
                pendingLoad: true,
                loading: false,
                loadError: null,
            };
            if (placeholder) {
                if (readMinimizedKeys().has(placeholder.key)) {
                    forgetMinimized(placeholder.key);
                    rememberMinimized(key);
                }
                return prev.map((t) => (t === placeholder ? nextThread : t));
            }
            return [...prev, nextThread];
        });
    }, []);

    const patchThread = useCallback((key, patch = {}) => {
        if (!key) return;
        setThreads((prev) => {
            let changed = false;
            const next = prev.map((t) => {
                if (t.key !== key) return t;
                const patched = patchObject(t, patch);
                if (patched !== t) changed = true;
                return patched;
            });
            return changed ? next : prev;
        });
    }, []);

    /** Move a chat to its real order-line key once we know it (keeps its window state). */
    const rekeyThread = useCallback((oldKey, newKey, patch = {}) => {
        if (!oldKey || !newKey) return;
        if (oldKey === newKey) {
            patchThread(oldKey, patch);
            return;
        }
        const previousHandlers = handlersRef.current[oldKey];
        if (previousHandlers) {
            const currentHandlers = handlersRef.current[newKey] || {};
            handlersRef.current[newKey] = {
                onAddComment: currentHandlers.onAddComment || previousHandlers.onAddComment,
                onDeleteComment: currentHandlers.onDeleteComment || previousHandlers.onDeleteComment,
                onResolveThread: currentHandlers.onResolveThread || previousHandlers.onResolveThread,
                onVisibleChange: currentHandlers.onVisibleChange || previousHandlers.onVisibleChange,
            };
            delete handlersRef.current[oldKey];
        }
        setThreads((prev) => {
            const source = prev.find((t) => t.key === oldKey);
            if (!source) return prev;
            const target = prev.find((t) => t.key === newKey);
            const merged = {
                ...(target || source),
                ...patch,
                key: newKey,
                minimized: source.minimized || readMinimizedKeys().has(source.key) || readMinimizedKeys().has(newKey)
                    ? true
                    : (target ? target.minimized : false),
                pos: (target || source).pos ?? source.pos ?? null,
            };
            if (readMinimizedKeys().has(oldKey)) {
                forgetMinimized(oldKey);
                rememberMinimized(newKey);
            }
            return prev
                .filter((t) => t.key !== oldKey && t.key !== newKey)
                .concat(merged);
        });
    }, [patchThread]);

    const notifyVisible = (key, isVisible) => {
        try {
            handlersRef.current[key]?.onVisibleChange?.(isVisible);
        } catch (err) {
            /* ignore parent presence errors */
        }
    };

    const minimizeThread = useCallback((key) => {
        if (!key) return;
        rememberMinimized(key);
        // A minimized chat is not open, so the live face on the row has to go away.
        notifyVisible(key, false);
        setThreads((prev) => {
            const next = prev.map((t) => (t.key === key ? { ...t, minimized: true, highlightCount: 0 } : t));
            persistThreads(next);
            return next;
        });
    }, []);

    const restoreThread = useCallback((key) => {
        if (!key) return;
        forgetMinimized(key);
        notifyVisible(key, true);
        setThreads((prev) => {
            const next = prev.map((t) => (t.key === key ? { ...t, minimized: false } : t));
            persistThreads(next);
            return next;
        });
    }, []);

    const closeThread = useCallback((key) => {
        if (!key) return;
        const handlers = handlersRef.current[key];
        try {
            handlers?.onVisibleChange?.(false);
        } catch (err) {
            /* ignore parent presence errors */
        }
        forgetMinimized(key);
        setThreads((prev) => {
            const next = prev.filter((t) => t.key !== key);
            persistThreads(next);
            return next;
        });
        delete handlersRef.current[key];
    }, []);

    const updateThreadComments = useCallback((key, comments) => {
        if (!key || !Array.isArray(comments)) return;
        setThreads((prev) => prev.map((t) => {
            if (t.key !== key) return t;
            const current = t.comments || [];
            if (current.length === comments.length
                && current.every((c, i) => String(c?.id) === String(comments[i]?.id)
                    && c?.comment === comments[i]?.comment
                    && (c?.lastEditedBy || '') === (comments[i]?.lastEditedBy || ''))) {
                return t.pendingLoad ? { ...t, pendingLoad: false } : t;
            }
            return { ...t, comments, pendingLoad: false };
        }));
    }, []);

    const updateThreadPosition = useCallback((key, pos) => {
        if (!key || !pos || !Number.isFinite(pos.left) || !Number.isFinite(pos.top)) return;
        setThreads((prev) => prev.map((t) => (
            t.key === key ? { ...t, pos: { left: pos.left, top: pos.top } } : t
        )));
    }, []);

    const value = useMemo(() => ({
        threads,
        openThread,
        ensureThread,
        patchThread,
        rekeyThread,
        minimizeThread,
        restoreThread,
        closeThread,
        updateThreadComments,
        updateThreadPosition,
        registerHandlers,
        getHandlers,
        isThreadOpen,
    }), [threads, openThread, ensureThread, patchThread, rekeyThread, minimizeThread, restoreThread, closeThread, updateThreadComments, updateThreadPosition, registerHandlers, getHandlers, isThreadOpen]);

    return (
        <CommentDockContext.Provider value={value}>
            {children}
        </CommentDockContext.Provider>
    );
}

export function useCommentDock() {
    return useContext(CommentDockContext);
}

export default CommentDockContext;
